import React, { useCallback } from "react";
import { includes, map } from "lodash";
import { Facet } from "@linkfluence/models/facet";
import Subtitle from "@radarly/components/cards/card/subtitle";
import { useCustomFields } from "@radarly/core/custom-field/use-cases";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import { TermMetricsCard } from "@radarly/models/cards";
import { createClauseFromInnerClause } from "@radarly/models/clause/creator";
import { getDateFromClause } from "@radarly/models/clause/getter";
import { isBubbleChartDataviz } from "@radarly/models/dataviz";
import { getTermFromAggs } from "@radarly/models/search";
import BubbleMetric from "@radarly/shared/cards/subtitle/bubble-metric";
import Date from "@radarly/shared/cards/subtitle/date";
import Tile from "@radarly/shared/cards/tile";
import BubbleChart from "@radarly/shared/widgets/term-metrics/bubble-chart";
import { useFacets } from "../facets";
import { useFragment } from "../fragment";
import { useGenerateCSV } from "../hooks/useGenerateCSV";
import { CardProps } from "../model";

const RadarlyTermMetricsCards = ({
  card,
  openPostsPanel,
  height,
  timezone,
  forceReload,
  nowDate,
  ...props
}: CardProps<TermMetricsCard>) => {
  const project = useCurrentProject();
  const fragment = useFragment(
    card.fragments[0],
    props.clause,
    props.from,
    props.to,
    timezone
  );
  const dataviz = card.dataviz;
  const term = getTermFromAggs(fragment.request.aggs) as Facet;
  const fields = useCustomFields();
  const title = card.title;
  const { onGenerateCSV } = useGenerateCSV(title, fragment);

  const onClickItem = useCallback(
    (id: string | number) => {
      const { from, to } = getDateFromClause(fragment.request.query);
      openPostsPanel({
        clause: fragment.request.query,
        period: {
          from,
          to,
        },
        title,
        timezone,
        focusClause: createClauseFromInnerClause(
          { [term]: [id] },
          includes(map(fields, "label"), term)
        ),
      });
    },
    [fragment.request.query, openPostsPanel, title, timezone, term, fields]
  );
  const facet = useFacets(term, fragment.request.query);
  return (
    <Tile
      title={title}
      height={height}
      subtitle={
        <Subtitle
          meta={
            isBubbleChartDataviz(dataviz)
              ? [
                  <Date
                    fragment={fragment}
                    timezone={timezone}
                    nowDate={nowDate}
                  />,
                  <BubbleMetric
                    metric={fragment.request.aggs[term].metrics[2]}
                  />,
                ]
              : [
                  <Date
                    fragment={fragment}
                    timezone={timezone}
                    nowDate={nowDate}
                  />,
                ]
          }
        />
      }
      clause={fragment.request.query}
      isPNG
      onGenerateCSV={onGenerateCSV}
    >
      <BubbleChart
        fragment={fragment}
        project={project.id}
        dataviz={dataviz}
        facet={facet}
        onClickPoint={openPostsPanel ? onClickItem : null}
        forceReload={forceReload}
      />
    </Tile>
  );
};

export default RadarlyTermMetricsCards;
